import { FC, ReactNode } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import { Message } from "shared/components/Message";
import { mapMessages } from "shared/mapMessages";

const messages = mapMessages("src.shared.components.AccordionSelection", {
    fieldsetLabel: "Select from {count} options",
    optionHelpText: "{index} of {count}",
});

type Option = string | null;

interface ISelectionOptionProps {
    helpText: ReactNode;
    message: MessageDescriptor;
    isOpen: boolean;
    name: string;
    option: string;
    children: ReactNode;
}

interface IProps {
    children: { options: Omit<ISelectionOptionProps, "helpText" | "isOpen" | "name">[] };
    name: string;
    initialSelection?: Option;
}

const SelectionOption: FC<ISelectionOptionProps> = ({
    children,
    helpText,
    message,
    name,
    option,
    isOpen,
}) => {
    const { formatMessage } = useIntl();

    return <details name={name} open={isOpen} className="card" data-testid={`AccordionSelection_${option}`}>
        <summary className="card-header">
            <Message message={message} />&nbsp;
            {helpText}
        </summary>
        <div className="card-body" role="region" aria-label={formatMessage(message)}>
            {children}
        </div>
    </details>;
};

export const AccordionSelection: FC<IProps> = ({
    children,
    initialSelection,
    name,
}) => {
    return <fieldset className="form-group">
        <legend className="sr-only"><Message message={messages.fieldsetLabel} values={{ count: children.options.length }} /></legend>
        <div className="accordion">
            {children.options.map((child, i) => (
                <SelectionOption
                    key={child.option}
                    {...child}
                    helpText={<span className="sr-only"><Message message={messages.optionHelpText} values={{ count: children.options.length, index: i + 1 }} /></span>}
                    name={name}
                    isOpen={initialSelection === child.option}
                />
            ))}
        </div>
    </fieldset>;
};
